import { FC } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormProvider, useForm } from 'react-hook-form';
import { Spinner } from 'ui/spinner';
import { useQueryParams } from 'utils/hooks/useQueryParams';
import { useQueryPlacementById } from 'modules/placements/hooks/useQueryPlacementById';
import { MediaLayout, PlacementType, PlacementsCreateFormState } from 'modules/placements/types';
import { SelectiveTargetingOptionValues } from 'modules/workflowNodeEditors/components/sendNodeEditors/BaseSendEditor/DeliverySection/SelectiveTargeting/SelectiveTargeting.constants';
import {
  getPlacementCreateDefaultValues,
  transformPlacementToFormValues,
} from 'modules/placements/utils';
import { LoadingContainer } from './styles';

const FormSchema = z.object({
  placementName: z.string().min(1, 'Required'),
  description: z.string().optional(),
  mediaLayout: z.nativeEnum(MediaLayout),
  placementType: z.nativeEnum(PlacementType),
  campaignLimit: z.number().min(1).max(10),
  hasTitle: z.boolean(),
  hasBody: z.boolean(),
  hasMedia: z.boolean(),
  hasDataFeeds: z.boolean(),
  hasOpenAction: z.boolean(),
  actionButtons: z.array(
    z.object({
      title: z.string(),
      isActive: z.boolean(),
    }),
  ),
  payload: z.string(),
  destinations: z.nativeEnum(SelectiveTargetingOptionValues),
  textFields: z.array(z.string()),
});

export const PlacementsCreateFormProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const defaultValues = getPlacementCreateDefaultValues();
  const methods = useForm<PlacementsCreateFormState>({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
    resolver: zodResolver(FormSchema),
  });

  const params = useQueryParams();
  const placementCopyBaseId = params.get('from');
  const { loading } = useQueryPlacementById(Number(placementCopyBaseId), (result) =>
    methods.reset(transformPlacementToFormValues(result.placement.payload.fields)),
  );

  return (
    <FormProvider {...methods}>
      {loading ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        children
      )}
    </FormProvider>
  );
};
